import React from 'react';

import { locationAssign } from '@betterment/js-runtime';
import { DropdownMenu, HStack, Icon, ShimmerText, Text, VStack } from '@betterment/samba-react';

import { trackClick } from '../../../../features/track';
import { LegacyTransferType } from '../../../../graphql/schema';
import { NarrowTypename } from '../../../../graphql/utils/narrow-typename';
import { useDirectTradingAccountInflowOptions } from '../../../../Shared/hooks/useDirectTradingAccountInflowOptions/useDirectTradingAccountInflowOptions';
import { urlBuilder } from '../../../../Shared/urlBuilder';
import { EnvelopeAccountTransactionOptionsQuery } from '../graphql/envelopeAccountTransactionOptions.generated';

type DirectTradingAccount = NarrowTypename<EnvelopeAccountTransactionOptionsQuery['account'], 'DirectTradingAccount'>;

type Options = NonNullable<ReturnType<typeof useDirectTradingAccountInflowOptions>>;

const LoadingItems = () => (
  <>
    <DropdownMenu.Item css={{ paddingY: 'space.100', width: '300px' }}>
      <ShimmerText textStyle="text50" />
    </DropdownMenu.Item>
    <DropdownMenu.Item css={{ paddingY: 'space.100', width: '300px' }}>
      <ShimmerText textStyle="text50" />
    </DropdownMenu.Item>
  </>
);

export const AddCashItems: React.FC<{ options: Options }> = ({ options }) => {
  const { depositOption, transferOption } = options;

  const navigateToDepositSetup = () => {
    const subAccountId = depositOption.destinationLegacySubAccountId;
    const returnTo = window.location.pathname;
    trackClick({
      name: 'Deposit',
      module: 'ManageCash',
    });
    locationAssign(urlBuilder.appDepositSetup.absolute({ subAccountId, returnTo }));
  };

  const navigateToTransferSetup = ({ option }: { option: NonNullable<Options['transferOption']> }) => {
    const returnTo = window.location.pathname;
    const { sourceLegacySubAccountId: subAccountId, destinationLegacySubAccountId: destinationAccountId } = option;
    trackClick({
      name: 'Transfer',
      module: 'ManageCash',
    });
    locationAssign(
      urlBuilder.showSubAccountTransferSetup.absolute({
        subAccountId,
        destination_account_id: destinationAccountId,
        returnTo,
      })
    );
  };

  return (
    <>
      <DropdownMenu.Item css={{ paddingY: 'space.100' }} onSelect={navigateToDepositSetup}>
        Deposit from a bank
      </DropdownMenu.Item>
      {transferOption && (
        <DropdownMenu.Item
          css={{ paddingY: 'space.100' }}
          onSelect={() => navigateToTransferSetup({ option: transferOption })}
        >
          <VStack>
            <Text>Transfer from a Betterment account</Text>
            {transferOption.instantTransferEligible && (
              <HStack css={{ gap: 'space.50' }}>
                <Icon name="Lightning" size="xxsmall" color="icon.success" />
                <Text variant="text50" color="text.success">
                  Instant transfer available
                </Text>
              </HStack>
            )}
          </VStack>
        </DropdownMenu.Item>
      )}
    </>
  );
};

export const AddCashOptions = ({ account }: { account: DirectTradingAccount }) => {
  const { id: accountId, transactionOptions } = account;

  const options = useDirectTradingAccountInflowOptions({ accountId });

  if (
    !transactionOptions.some(
      o => o.__typename === 'TransferOption' && o.legacyTransferType === LegacyTransferType.Deposit
    )
  ) {
    return null;
  }

  return <>{options ? <AddCashItems options={options} /> : <LoadingItems />}</>;
};
